/**
 * GIF89a encoder.
 *
 * Written rather than pulled in, for the same reason as the ZIP writer: what we need is
 * a narrow slice of the format, and the general-purpose libraries all carry a colour
 * quantiser we would have to work around anyway.
 *
 * Three decisions shape the output:
 *
 *  - **One global palette for the whole animation.** Per-frame palettes are marginally
 *    more accurate but make the colours *shimmer* between frames, which is exactly the
 *    artefact that makes a generative loop look cheap. A shared palette also means the
 *    colour table is written once instead of once per frame.
 *  - **Median cut, not NeuQuant.** Median cut is deterministic — the same composition
 *    encodes to the same bytes every time, which matters for a tool where reproducibility
 *    is a feature. NeuQuant's neural pass is stochastic and slower for no visible gain at
 *    these palette sizes.
 *  - **Ordered dithering, optional.** A Bayer matrix is deterministic too, and it breaks
 *    up the banding that 256 colours produce on the smooth gradients this app is full of.
 *    Error diffusion would look better on a still image but crawls between frames.
 */

/** `Uint8Array<ArrayBuffer>` — the concrete form `BlobPart` accepts. */
type Bytes = Uint8Array<ArrayBuffer>;

// ── Byte sink ────────────────────────────────────────────────────────────────

class ByteWriter {
  private buffer: Bytes;
  private length = 0;

  constructor(capacity = 1 << 16) {
    this.buffer = new Uint8Array(Math.max(1024, Math.ceil(capacity))) as Bytes;
  }

  private ensure(extra: number): void {
    if (this.length + extra <= this.buffer.length) return;
    let capacity = this.buffer.length * 2;
    while (capacity < this.length + extra) capacity *= 2;
    const next = new Uint8Array(capacity) as Bytes;
    next.set(this.buffer.subarray(0, this.length));
    this.buffer = next;
  }

  byte(value: number): void {
    this.ensure(1);
    this.buffer[this.length++] = value & 0xff;
  }

  /** Little-endian 16-bit, which is what every numeric field in GIF uses. */
  short(value: number): void {
    this.byte(value);
    this.byte(value >> 8);
  }

  bytes(values: ArrayLike<number>): void {
    this.ensure(values.length);
    this.buffer.set(values as Uint8Array, this.length);
    this.length += values.length;
  }

  ascii(text: string): void {
    for (let i = 0; i < text.length; i++) this.byte(text.charCodeAt(i));
  }

  get size(): number {
    return this.length;
  }

  finish(): Bytes {
    return this.buffer.slice(0, this.length) as Bytes;
  }
}

// ── Median-cut quantisation ──────────────────────────────────────────────────

interface Box {
  /** Indices into the sample arrays. */
  from: number;
  to: number;
  rMin: number;
  rMax: number;
  gMin: number;
  gMax: number;
  bMin: number;
  bMax: number;
}

interface Samples {
  r: Uint8Array;
  g: Uint8Array;
  b: Uint8Array;
  order: Uint32Array;
  count: number;
}

function boxBounds(s: Samples, box: Box): void {
  let rMin = 255;
  let rMax = 0;
  let gMin = 255;
  let gMax = 0;
  let bMin = 255;
  let bMax = 0;
  for (let i = box.from; i < box.to; i++) {
    const p = s.order[i];
    const r = s.r[p];
    const g = s.g[p];
    const b = s.b[p];
    if (r < rMin) rMin = r;
    if (r > rMax) rMax = r;
    if (g < gMin) gMin = g;
    if (g > gMax) gMax = g;
    if (b < bMin) bMin = b;
    if (b > bMax) bMax = b;
  }
  box.rMin = rMin;
  box.rMax = rMax;
  box.gMin = gMin;
  box.gMax = gMax;
  box.bMin = bMin;
  box.bMax = bMax;
}

/**
 * Splits the colour cube repeatedly along the longest axis of the box with the widest
 * spread, taking the median so each box holds a similar number of pixels.
 *
 * Channels are weighted by luminance sensitivity when choosing the axis — splitting a
 * box that is wide in blue buys much less perceived accuracy than the same width in
 * green, and ignoring that wastes palette entries on shades nobody can tell apart.
 */
function medianCut(s: Samples, maxColors: number): Uint8Array {
  const first: Box = { from: 0, to: s.count, rMin: 0, rMax: 0, gMin: 0, gMax: 0, bMin: 0, bMax: 0 };
  boxBounds(s, first);
  const boxes: Box[] = [first];

  const priority = (box: Box): number => {
    const dr = (box.rMax - box.rMin) * 0.299;
    const dg = (box.gMax - box.gMin) * 0.587;
    const db = (box.bMax - box.bMin) * 0.114;
    // Weight by population as well, so a large flat region is not split before a tiny
    // rainbow that occupies four pixels.
    return Math.max(dr, dg, db) * Math.cbrt(box.to - box.from);
  };

  while (boxes.length < maxColors) {
    let best = -1;
    let bestScore = 0;
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].to - boxes[i].from < 2) continue;
      const score = priority(boxes[i]);
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0 || bestScore <= 0) break;

    const box = boxes[best];
    const dr = (box.rMax - box.rMin) * 0.299;
    const dg = (box.gMax - box.gMin) * 0.587;
    const db = (box.bMax - box.bMin) * 0.114;
    const channel = dg >= dr && dg >= db ? s.g : dr >= db ? s.r : s.b;

    // Sort just this box's slice by the chosen channel, then cut at the median.
    const slice = Array.from(s.order.subarray(box.from, box.to));
    slice.sort((a, b) => channel[a] - channel[b]);
    s.order.set(slice, box.from);

    const mid = box.from + (slice.length >> 1);
    const left: Box = { ...box, to: mid };
    const right: Box = { ...box, from: mid };
    boxBounds(s, left);
    boxBounds(s, right);
    boxes[best] = left;
    boxes.push(right);
  }

  const palette = new Uint8Array(boxes.length * 3);
  boxes.forEach((box, i) => {
    let r = 0;
    let g = 0;
    let b = 0;
    const n = Math.max(1, box.to - box.from);
    for (let k = box.from; k < box.to; k++) {
      const p = s.order[k];
      r += s.r[p];
      g += s.g[p];
      b += s.b[p];
    }
    palette[i * 3] = Math.round(r / n);
    palette[i * 3 + 1] = Math.round(g / n);
    palette[i * 3 + 2] = Math.round(b / n);
  });
  return palette;
}

/**
 * Nearest-palette-entry lookup with a 15-bit cache.
 *
 * A full 24-bit cache would be 16 MB and mostly empty; 5 bits per channel collapses the
 * lookup to 32 768 slots, and the quantisation error it introduces is well under one
 * palette step at any palette size GIF allows.
 */
class PaletteMap {
  private cache = new Int16Array(32768).fill(-1);

  constructor(private readonly palette: Uint8Array) {}

  nearest(r: number, g: number, b: number): number {
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const cached = this.cache[key];
    if (cached >= 0) return cached;

    let best = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < this.palette.length; i += 3) {
      const dr = r - this.palette[i];
      const dg = g - this.palette[i + 1];
      const db = b - this.palette[i + 2];
      // Weighted squared distance in the same luminance ratios used for splitting.
      const d = dr * dr * 0.299 + dg * dg * 0.587 + db * db * 0.114;
      if (d < bestDistance) {
        bestDistance = d;
        best = i / 3;
      }
    }
    this.cache[key] = best;
    return best;
  }

  /** The palette colour a pixel maps to, for measuring how far off it is. */
  entry(r: number, g: number, b: number): [number, number, number] {
    const i = this.nearest(r, g, b) * 3;
    return [this.palette[i], this.palette[i + 1], this.palette[i + 2]];
  }
}

/** 8×8 Bayer threshold matrix, normalised to −0.5…+0.5. */
const BAYER = (() => {
  const m = new Float32Array(64);
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      // The recursive construction, unrolled: for each bit from most to least
      // significant, append the two bits (y⊕x, y).
      let v = 0;
      for (let bit = 2; bit >= 0; bit--) {
        const bx = (x >> bit) & 1;
        const by = (y >> bit) & 1;
        v = (v << 2) | ((by ^ bx) << 1) | by;
      }
      m[y * 8 + x] = v / 64 - 0.5;
    }
  }
  return m;
})();

// ── LZW ──────────────────────────────────────────────────────────────────────

/**
 * GIF's variable-code-width LZW.
 *
 * The dictionary is a flat `Int32Array` indexed by `prefix << 8 | suffix` rather than a
 * Map. It is 1 M entries (4 MB) and has to be cleared whenever the dictionary fills,
 * but a typed-array read beats a Map lookup by enough that this is still the faster
 * arrangement for the millions of lookups a frame costs. It is allocated once and reused
 * across frames — a hundred-frame animation would otherwise churn 400 MB through the
 * collector for nothing.
 *
 * Zero means "empty", so stored codes carry a +1 offset.
 */
let lzwDict: Int32Array | null = null;

function lzwEncode(indices: Uint8Array, minCodeSize: number, out: ByteWriter): void {
  const clearCode = 1 << minCodeSize;
  const endCode = clearCode + 1;

  const dict = (lzwDict ??= new Int32Array(1 << 20));
  dict.fill(0);
  let next = endCode + 1;
  let codeSize = minCodeSize + 1;

  // Codes are packed LSB-first, then flushed in 255-byte sub-blocks.
  const block = new Uint8Array(255);
  let blockLength = 0;
  let accumulator = 0;
  let accumulatorBits = 0;

  const flushBlock = (): void => {
    if (blockLength === 0) return;
    out.byte(blockLength);
    out.bytes(block.subarray(0, blockLength));
    blockLength = 0;
  };

  const emit = (code: number): void => {
    accumulator |= code << accumulatorBits;
    accumulatorBits += codeSize;
    while (accumulatorBits >= 8) {
      block[blockLength++] = accumulator & 0xff;
      if (blockLength === 255) flushBlock();
      accumulator >>>= 8;
      accumulatorBits -= 8;
    }
  };

  out.byte(minCodeSize);
  emit(clearCode);

  let prefix = indices.length ? indices[0] : -1;
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i];
    const key = (prefix << 8) | k;
    const found = dict[key];
    if (found !== 0) {
      prefix = found - 1;
      continue;
    }

    emit(prefix);
    dict[key] = next + 1;
    next += 1;

    // ── Where the code width grows ───────────────────────────────────────────
    //
    // `next > 1 << codeSize`, deliberately, and not the `===` that looks right.
    //
    // The encoder adds a dictionary entry with every code it writes; the decoder only
    // adds one from its *second* code onwards, because the first has no predecessor to
    // extend. Its dictionary therefore trails by exactly one entry, and a width that
    // grows in step with the encoder's counter is one code too early for it. The rule
    // below keeps `codeSize` while `next ≤ 2^codeSize` — which is precisely the width
    // that still covers the largest code the decoder could be asked to read.
    //
    // Verified against a browser's own GIF decoder: with `===` a 9600-pixel frame
    // decodes 190 pixels and then garbage, with `>` all 9600 come back byte-exact.
    if (codeSize < 12) {
      if (next > 1 << codeSize) codeSize += 1;
    } else if (next >= 1 << 12) {
      // The dictionary is full — 4095 is the last legal code, so reset both sides of
      // the conversation before one larger is assigned.
      emit(clearCode);
      dict.fill(0);
      next = endCode + 1;
      codeSize = minCodeSize + 1;
    }
    prefix = k;
  }

  if (prefix >= 0) emit(prefix);
  emit(endCode);

  // Flush the partial byte, then the partial sub-block, then the terminator.
  if (accumulatorBits > 0) {
    block[blockLength++] = accumulator & 0xff;
    if (blockLength === 255) flushBlock();
  }
  flushBlock();
  out.byte(0);
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface GifFrame {
  data: Uint8ClampedArray;
  /** Frame duration in milliseconds. GIF stores this in 10 ms units. */
  delayMs: number;
}

export interface GifOptions {
  width: number;
  height: number;
  /** 2–256. Fewer colours mean a smaller file and visible banding. */
  colors?: number;
  dither?: boolean;
  /** 0 repeats forever, which is what a generative loop wants. */
  loopCount?: number;
  onProgress?: (progress: number) => void;
}

/**
 * Collects the colours the palette is built from.
 *
 * Every frame contributes, but subsampled: a 1080×1350 frame is 1.5 M pixels and the
 * median cut converges on the same boxes from a few tens of thousands. The stride is
 * prime-ish relative to the row length so the samples do not land in a column pattern.
 */
function collectSamples(frames: readonly GifFrame[], total: number): Samples {
  const budget = 60000;
  const stride = Math.max(1, Math.floor((total * frames.length) / budget));
  const capacity = Math.ceil((total * frames.length) / stride) + 8;

  const r = new Uint8Array(capacity);
  const g = new Uint8Array(capacity);
  const b = new Uint8Array(capacity);
  let count = 0;

  for (const frame of frames) {
    for (let p = 0; p < total && count < capacity; p += stride) {
      const i = p * 4;
      if (frame.data[i + 3] < 128) continue;
      r[count] = frame.data[i];
      g[count] = frame.data[i + 1];
      b[count] = frame.data[i + 2];
      count += 1;
    }
  }

  const order = new Uint32Array(count);
  for (let i = 0; i < count; i++) order[i] = i;
  return { r, g, b, order, count };
}

/**
 * Mean absolute per-channel error the palette makes, from a subsample.
 *
 * Sampled across the whole animation rather than the first frame: a global palette has
 * to cover every frame, and measuring only the first would underestimate its error
 * wherever the composition changes colour as it runs.
 */
function measureQuantisationError(frames: readonly GifFrame[], total: number, map: PaletteMap): number {
  const stride = Math.max(1, Math.floor((total * frames.length) / 4000));
  let sum = 0;
  let count = 0;
  for (const frame of frames) {
    for (let p = 0; p < total; p += stride) {
      const i = p * 4;
      if (frame.data[i + 3] < 128) continue;
      const entry = map.entry(frame.data[i], frame.data[i + 1], frame.data[i + 2]);
      sum +=
        Math.abs(frame.data[i] - entry[0]) +
        Math.abs(frame.data[i + 1] - entry[1]) +
        Math.abs(frame.data[i + 2] - entry[2]);
      count += 3;
    }
  }
  return count ? sum / count : 0;
}

export function encodeGif(frames: readonly GifFrame[], options: GifOptions): Blob {
  const { width, height } = options;
  if (!frames.length) throw new Error('Keine Frames zum Kodieren.');

  const total = width * height;
  const dither = options.dither !== false;

  // A transparent index is only reserved when something is actually transparent —
  // spending one of 256 slots on it otherwise costs a colour for nothing.
  let hasTransparency = false;
  for (const frame of frames) {
    for (let p = 0; p < total; p++) {
      if (frame.data[p * 4 + 3] < 128) {
        hasTransparency = true;
        break;
      }
    }
    if (hasTransparency) break;
  }

  const maxColors = Math.max(2, Math.min(256, options.colors ?? 256)) - (hasTransparency ? 1 : 0);
  const samples = collectSamples(frames, total);
  // A fully transparent animation has no colours to cut; one black entry keeps the
  // colour table legal.
  const palette = samples.count > 0 ? medianCut(samples, maxColors) : new Uint8Array(3);
  const colorCount = palette.length / 3;
  const transparentIndex = hasTransparency ? colorCount : -1;
  const tableSize = colorCount + (hasTransparency ? 1 : 0);

  // GIF colour tables are a power of two, at least 2 entries.
  let depth = 1;
  while (1 << depth < tableSize) depth += 1;
  const tableEntries = 1 << depth;

  const map = new PaletteMap(palette);

  /**
   * Dither amplitude, derived from the palette's *actual* accuracy.
   *
   * The obvious formula — one step of a uniform 256-colour cube — is wrong here, because
   * a median-cut palette is adaptive: on the smooth gradients this app produces, 256
   * chosen colours land within about three levels of every pixel, and dithering against
   * a step forty levels wide adds far more noise than it removes banding. Measuring the
   * error the palette really makes, and spreading exactly that, means the same code
   * dithers hard at 16 colours and barely at all at 256.
   *
   * Half a step rather than a whole one, and capped: dithering costs file size — it
   * breaks up the runs LZW lives on, and a fully dithered frame can be several times
   * larger — so it is worth spending only where there is banding to hide.
   */
  const ditherAmplitude = dither ? Math.min(24, measureQuantisationError(frames, total, map) * 2) : 0;

  const out = new ByteWriter(total * frames.length * 0.4 + 4096);

  // ── Header and logical screen descriptor ───────────────────────────────────
  out.ascii('GIF89a');
  out.short(width);
  out.short(height);
  out.byte(0x80 | (depth - 1)); // global colour table present, `depth` bits per entry
  out.byte(0); // background colour index
  out.byte(0); // pixel aspect ratio: none
  for (let i = 0; i < tableEntries; i++) {
    out.byte(palette[i * 3] ?? 0);
    out.byte(palette[i * 3 + 1] ?? 0);
    out.byte(palette[i * 3 + 2] ?? 0);
  }

  // ── NETSCAPE2.0 loop extension ─────────────────────────────────────────────
  const loopCount = options.loopCount ?? 0;
  out.byte(0x21);
  out.byte(0xff);
  out.byte(11);
  out.ascii('NETSCAPE2.0');
  out.byte(3);
  out.byte(1);
  out.short(loopCount);
  out.byte(0);

  const minCodeSize = Math.max(2, depth);
  const indices = new Uint8Array(total);

  frames.forEach((frame, frameIndex) => {
    // ── Index the frame ──────────────────────────────────────────────────────
    for (let y = 0; y < height; y++) {
      const rowOffset = (y & 7) * 8;
      for (let x = 0; x < width; x++) {
        const p = y * width + x;
        const i = p * 4;
        if (frame.data[i + 3] < 128 && transparentIndex >= 0) {
          indices[p] = transparentIndex;
          continue;
        }
        if (ditherAmplitude < 1) {
          indices[p] = map.nearest(frame.data[i], frame.data[i + 1], frame.data[i + 2]);
          continue;
        }
        const t = BAYER[rowOffset + (x & 7)] * ditherAmplitude;
        indices[p] = map.nearest(
          clampByte(frame.data[i] + t),
          clampByte(frame.data[i + 1] + t),
          clampByte(frame.data[i + 2] + t),
        );
      }
    }

    // ── Graphic control extension ────────────────────────────────────────────
    out.byte(0x21);
    out.byte(0xf9);
    out.byte(4);
    // Disposal 2 (restore to background) keeps transparent frames from smearing into
    // each other; every frame is written full-size, so nothing is lost by it.
    out.byte((2 << 2) | (transparentIndex >= 0 ? 1 : 0));
    out.short(Math.max(2, Math.round(frame.delayMs / 10)));
    out.byte(transparentIndex >= 0 ? transparentIndex : 0);
    out.byte(0);

    // ── Image descriptor ─────────────────────────────────────────────────────
    out.byte(0x2c);
    out.short(0);
    out.short(0);
    out.short(width);
    out.short(height);
    out.byte(0); // no local colour table, not interlaced

    lzwEncode(indices, minCodeSize, out);
    options.onProgress?.((frameIndex + 1) / frames.length);
  });

  out.byte(0x3b); // trailer
  return new Blob([out.finish()], { type: 'image/gif' });
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

/**
 * A GIF's minimum frame delay is 10 ms, and browsers historically clamp anything under
 * 20 ms to 100 ms. Callers use this to warn instead of silently producing a loop that
 * plays at a tenth of the requested speed.
 */
export const GIF_MIN_DELAY_MS = 20;
